// Community Contacts Service - Handles Personal Toolbox community integration

export interface CommunityContact {
  id: string;
  name: string;
  avatar: string;
  status: 'online' | 'offline' | 'away';
  lastSeen?: Date;
  isFavorite: boolean;
  relationship: 'neighbor' | 'friend' | 'family' | 'colleague';
  location?: string; // Apartment/house number
}

export interface ShareMessage {
  id: string;
  projectId: string;
  projectTitle: string;
  projectUrl: string;
  message?: string;
  timestamp: Date;
  senderId: string;
  recipientId: string;
}

// Mock data for development
const MOCK_CONTACTS: CommunityContact[] = [
  {
    id: '1',
    name: 'John Walker',
    relationship: 'friend',
    location: 'Apt 2B',
    status: 'online',
    avatar: 'J',
    isFavorite: true,
    lastSeen: new Date(),
  },
  {
    id: '2',
    name: 'Sarah Mitchell',
    relationship: 'neighbor',
    location: 'House 15',
    status: 'online',
    avatar: 'S',
    isFavorite: true,
    lastSeen: new Date(),
  },
  {
    id: '3',
    name: 'Maria Rodriguez',
    relationship: 'family',
    location: 'House 8',
    status: 'offline',
    avatar: 'M',
    isFavorite: true,
    lastSeen: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
  },
  {
    id: '4',
    name: 'David Chen',
    relationship: 'colleague',
    location: 'Apt 4A',
    status: 'away',
    avatar: 'D',
    isFavorite: false,
    lastSeen: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
  },
  {
    id: '5',
    name: 'Michael Thompson',
    relationship: 'neighbor',
    location: 'Apt 1C',
    status: 'online',
    avatar: 'M',
    isFavorite: false,
    lastSeen: new Date(),
  },
  {
    id: '6',
    name: 'Lisa Johnson',
    relationship: 'friend',
    location: 'House 22',
    status: 'offline',
    avatar: 'L',
    isFavorite: false,
    lastSeen: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
  },
];

export const communityContactsService = {
  async getCommunityContacts(): Promise<CommunityContact[]> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 100));
    return MOCK_CONTACTS;
  },

  async getFavoriteContacts(): Promise<CommunityContact[]> {
    const contacts = await this.getCommunityContacts();
    return contacts.filter((contact) => contact.isFavorite);
  },

  async searchContacts(query: string): Promise<CommunityContact[]> {
    const contacts = await this.getCommunityContacts();
    const lowercaseQuery = query.toLowerCase();
    return contacts.filter(
      (contact) =>
        contact.name.toLowerCase().includes(lowercaseQuery) ||
        (contact.location && contact.location.toLowerCase().includes(lowercaseQuery)),
    );
  },

  async shareToContact(
    recipientId: string,
    projectId: string,
    projectTitle: string,
    projectUrl: string,
    personalMessage?: string,
  ): Promise<boolean> {
    // Simulate API call with delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Simulate 95% success rate
    const success = Math.random() > 0.05;

    if (success) {
      console.log('Project shared successfully:', {
        recipientId,
        projectId,
        projectTitle,
        personalMessage,
      });
    }

    return success;
  },

  async shareToMultipleContacts(
    contactIds: string[],
    projectId: string,
    projectTitle: string,
    projectUrl: string,
    personalMessage?: string,
  ): Promise<{ success: string[]; failed: string[] }> {
    const results = await Promise.allSettled(
      contactIds.map((id) =>
        this.shareToContact(id, projectId, projectTitle, projectUrl, personalMessage),
      ),
    );

    const success: string[] = [];
    const failed: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        success.push(contactIds[index]);
      } else {
        failed.push(contactIds[index]);
      }
    });

    return { success, failed };
  },

  formatLastSeen(lastSeen?: Date): string {
    if (!lastSeen) return 'online';

    const now = new Date();
    const diffMs = now.getTime() - lastSeen.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      return 'online';
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return lastSeen.toLocaleDateString();
    }
  },

  getStatusColor(status: CommunityContact['status']): string {
    switch (status) {
      case 'online':
        return 'bg-green-500';
      case 'away':
        return 'bg-yellow-500';
      case 'offline':
      default:
        return 'bg-gray-400';
    }
  },
};
